import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Transition } from '@headlessui/react';
import {
  ChatGroupData,
  getUserID,
  getUserType,
  researchListChatGroups,
  researchGetAvailableSources,
  ResearchChatGroup,
  ResearchSource,
} from '../../store/actions/ChatAction';
import { useSelector } from 'react-redux';
import { fetchStructure } from '../../store/actions/FolderAction';
import { RootState, useAppDispatch } from '../../store';

import AISettingsComponent from '../Settings/SettingsTabs/AISettings';
import ApiLoader from '../ApiLoader';
import { FileProps } from '../Files/documents/types';
import AIFolderList from './AIFolderList';
import { Tooltip } from '@mui/material';
import { AiOutlineDelete } from 'react-icons/ai';
import Badge from '../shared/TailwindComponents/Badge';
import Input from '../shared/TailwindComponents/Input';
import Button from '../shared/TailwindComponents/Button';
import { PiGlobeLight, PiDatabaseLight, PiCaretDownLight, PiCaretRightLight, PiCheckLight } from 'react-icons/pi';
import Flag from '../shared/TailwindComponents/Flag';

const groupChatsByTime = (chats: ChatGroupData[], today: Date) => {
  const groups: { [key: string]: ChatGroupData[] } = {
    "Today": [],
    "This Week": [],
    "Last Week": [],
    "Last 30 Days": [],
    "Older": [],
  };

  chats.forEach((chat) => {
    const dateStr = chat.LastModified || chat.last_message_at || chat.updated_at || chat.created_at || '';
    const date = new Date(dateStr);
    const diffMs = today.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      groups["Today"].push(chat);
    } else if (diffDays <= 7) {
      groups["This Week"].push(chat);
    } else if (diffDays <= 14) {
      groups["Last Week"].push(chat);
    } else if (diffDays <= 30) {
      groups["Last 30 Days"].push(chat);
    } else {
      groups["Older"].push(chat);
    }
  });

  return groups;
};

// Country display names
const countryName = (code: string): string => {
  const names: Record<string, string> = {
    DE: 'Germany', NO: 'Norway', PL: 'Poland', ES: 'Spain',
    SE: 'Sweden', GB: 'United Kingdom', US: 'United States',
    FR: 'France', IT: 'Italy', NL: 'Netherlands', FI: 'Finland',
    DK: 'Denmark', AT: 'Austria', CH: 'Switzerland', BE: 'Belgium',
  };
  return names[code?.toUpperCase()] || code;
};

// Format document count
const formatCount = (count?: number): string => {
  if (!count) return '';
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(0)}K`;
  return `${count}`;
};

interface AISidebarProps {
  onFileSelect: (file: FileProps) => void;
  currentView: 'Folders' | 'Projects' | 'ChatHistory' | 'AISettings' | 'Sources';
  onSelectChatGroup: (chatGroupId: string) => void;
  handleDeleteChat: (chatGroupId: string) => void;
  onProjectSelect: (projectId: string | null) => void;
  selectedSources?: ResearchSource[];
  onSourcesChange?: (sources: ResearchSource[]) => void;
  onClose?: () => void;
  isChatLoading?: boolean;
}

const AISidebar: React.FC<AISidebarProps> = ({
  onFileSelect,
  currentView,
  onSelectChatGroup,
  handleDeleteChat,
  onProjectSelect,
  selectedSources = [],
  onSourcesChange,
  onClose,
  isChatLoading = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userType = getUserType();
  const userId = getUserID();
  const structure = useSelector((state: RootState) => state.folders.structure);
  const chatGroups = useSelector((state: RootState) => state.model.chatGroups);
  const selectedModel = useSelector((state: RootState) => state.model.selectedModel);
  const isFoldersLoading = useSelector((state: RootState) => state.folders.loading);
  const selectedChatGroupId = useSelector((state: RootState) => state.model.chatGroupID);
  const [searchQuery, setSearchQuery] = useState('');
  const today = new Date();

  // Sources view state
  const [jurisdictionSources, setJurisdictionSources] = useState<ResearchSource[]>([]);
  const [userDatasources, setUserDatasources] = useState<ResearchSource[]>([]);
  const [isLoadingSources, setIsLoadingSources] = useState(false);
  const [localSelectedSources, setLocalSelectedSources] = useState<ResearchSource[]>(selectedSources);
  const [expandedCountries, setExpandedCountries] = useState<Record<string, boolean>>({});
  const [isJurisdictionsOpen, setIsJurisdictionsOpen] = useState(true);
  const [isDataSourcesOpen, setIsDataSourcesOpen] = useState(true);

  // Sync local selection with parent when opening
  useEffect(() => {
    if (currentView === 'Sources') {
      setLocalSelectedSources(selectedSources);
    }
  }, [currentView, selectedSources]);

  useEffect(() => {
    if (currentView === 'Folders') {
      // dispatch(fetchStructure());
    } else if (currentView === 'ChatHistory' && chatGroups.length === 0) {
      dispatch(researchListChatGroups());
    } else if (currentView === 'Sources' && jurisdictionSources.length === 0) {
      loadSources();
    }
  }, [currentView, dispatch, userId, userType, selectedModel, chatGroups.length]);

  const loadSources = async () => {
    try {
      setIsLoadingSources(true);
      const result = await dispatch(researchGetAvailableSources());
      setJurisdictionSources(result.jurisdiction_sources || []);
      setUserDatasources(result.user_datasources || []);
    } catch (err) {
      console.error('Failed to load sources:', err);
    } finally {
      setIsLoadingSources(false);
    }
  };

  const isSourceSelected = (source: ResearchSource) =>
    localSelectedSources.some(
      (s) => s.source_type === source.source_type && s.source_identifier === source.source_identifier &&
        (s.index_name ? s.index_name === source.index_name : true)
    );

  const toggleSource = (source: ResearchSource) => {
    // Only block selection for datasources that are not ready/active
    if (source.source_type === 'user_datasource' && source.status && source.status !== 'ready' && source.status !== 'active') return;
    if (isSourceSelected(source)) {
      setLocalSelectedSources(
        localSelectedSources.filter(
          (s) => !(s.source_type === source.source_type && s.source_identifier === source.source_identifier &&
            (s.index_name ? s.index_name === source.index_name : true))
        )
      );
    } else {
      setLocalSelectedSources([...localSelectedSources, source]);
    }
  };

  const handleApplySources = () => {
    onSourcesChange?.(localSelectedSources);
    onClose?.();
  };

  // Group jurisdiction sources by country code
  const jurisdictionsByCountry = jurisdictionSources.reduce<Record<string, ResearchSource[]>>((acc, src) => {
    const country = src.source_identifier;
    if (!acc[country]) acc[country] = [];
    acc[country].push(src);
    return acc;
  }, {});

  const toggleCountryExpand = (code: string) => {
    setExpandedCountries((prev) => ({ ...prev, [code]: !prev[code] }));
  };





  const handleSelectChatGroup = (chatGroupId: string) => {
    onSelectChatGroup(chatGroupId);
  };


  const filteredChatGroups = chatGroups.filter((chatGroup: ChatGroupData) =>
    (chatGroup.ChatTitle || chatGroup.title || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const searchFoldersAndFiles = (folders: any, query: string) => {
    const filteredFolders: any[] = [];
  
    folders.forEach((folder: any) => {
      const folderNameMatches = folder.FolderName.toLowerCase().includes(query.toLowerCase());
      const subFolders = folder.SubFolders ? searchFoldersAndFiles(folder.SubFolders, query) : [];
      const files = folder.Files
        ? folder.Files.filter((file: any) =>
            file.FileName.toLowerCase().includes(query.toLowerCase())
          )
        : [];
  
      const shouldOpen = folderNameMatches || subFolders.some((sf) => sf.shouldOpen) || files.length > 0;
  
      if (shouldOpen) {
        filteredFolders.push({
          ...folder,
          SubFolders: subFolders,
          Files: files,
          shouldOpen,
        });
      }
    });
  
    return filteredFolders;
  };

  const filteredStructure = searchFoldersAndFiles(structure, searchQuery);
  const groupedChats = groupChatsByTime(filteredChatGroups, today);

  const formatDate = (lastModified: string, today: Date) => {
    const date = new Date(lastModified);
    const diffMs = today.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return t('Today');
    if (diffDays === 1) return t('Yesterday');
    if (diffDays <= 7) return date.toLocaleDateString(undefined, { weekday: 'long' });
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <aside className={`${currentView === 'Sources' ? 'w-80' : 'w-64'} h-full shadow-md dark:shadow-dark-md bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col transition-all duration-200`}>
      <div className="flex-shrink-0">
        {currentView === 'Sources' && (
          <div className="px-3 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{t('Research Sources')}</h3>
            <p className="text-xs text-gray-400 mt-0.5">{t('Select jurisdictions and data sources')}</p>
          </div>
        )}
        {currentView === "ChatHistory" && (
          <div className="px-2 my-2">
          <Input
            type="text"
            placeholder={t('Search...')}
            className="w-full px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            />
        </div>
          )}
      </div>

      <div className={`flex-1 ${currentView === "AISettings" ? 'overflow-y-visible' : 'overflow-y-auto'}`}>
        {currentView === 'Folders' && (
          <div className="p-2">
            {isFoldersLoading ? (
              <div className="flex justify-center items-center h-full">
                <ApiLoader />
              </div>
            ) : (
              <AIFolderList
                folders={filteredStructure}
                onSelectFolder={() => {}}
                selectedFolderId={null}
                isLoading={false}
                onFileSelect={onFileSelect}
              />
            )}
          </div>
        )}
      {currentView === 'ChatHistory' && (
          <div className="p-2">
            {isChatLoading ? (
              <div className="flex justify-center items-center h-full">
                <ApiLoader />
              </div>
            ) : filteredChatGroups.length > 0 ? (
              <ul className="space-y-2">
                {Object.entries(groupedChats)
                  .filter(([_, chats]) => chats.length > 0)
                  .map(([period, chats]) => (
                    <Fragment key={period}>
                      <Transition
                        appear={true}
                        show={true}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-2"
                        enterTo="opacity-100 translate-y-0"
                      >
                        <li className="px-2 py-1 text-md font-semibold text-gray-500 dark:text-gray-400 sticky top-0 z-10 bg-white dark:bg-gray-800">
                          {t(period)}
                        </li>
                      </Transition>
                      {chats.map((chatGroup: ChatGroupData) => {
                        const chatId = chatGroup.ChatGroupID || chatGroup.uuid || '';
                        const chatTitle = chatGroup.ChatTitle || chatGroup.title || t('Untitled');
                        const lastModified = chatGroup.LastModified || chatGroup.last_message_at || chatGroup.updated_at || chatGroup.created_at || '';
                        const queryCount = chatGroup.total_messages ? Math.ceil(chatGroup.total_messages / 2) : undefined;
                        const sources = chatGroup.sources || [];
                        return (
                        <Transition
                          key={chatId}
                          appear={true}
                          show={true}
                          enter="transition ease-out duration-300"
                          enterFrom="opacity-0 translate-y-4"
                          enterTo="opacity-100 translate-y-0"
                          leave="transition ease-in duration-200"
                          leaveFrom="opacity-100 translate-y-0"
                          leaveTo="opacity-0 translate-y-[-4]"
                        >
                          <li
                            className={`flex items-center justify-between p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer ${
                              selectedChatGroupId === chatId
                                ? 'bg-gray-200 dark:bg-gray-600'
                                : ''
                            }`}
                          >
                            <div
                              onClick={() => handleSelectChatGroup(chatId)}
                              className="flex-1 min-w-0"
                            >
                              <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                                {chatTitle}
                              </p>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {formatDate(lastModified, today)}
                                </p>
                                {queryCount !== undefined && queryCount > 0 && (
                                  <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                    · {queryCount} {queryCount !== 1 ? t('queries') : t('query')}
                                  </span>
                                )}
                              </div>
                              {sources.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {sources.slice(0, 3).map((src, i) => (
                                    <span
                                      key={i}
                                      className="inline-block px-1.5 py-0 text-[9px] font-medium rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 truncate max-w-[80px]"
                                      title={src.source_name || src.source_identifier}
                                    >
                                      {src.source_name || src.source_identifier}
                                    </span>
                                  ))}
                                  {sources.length > 3 && (
                                    <span className="text-[9px] text-gray-400">+{sources.length - 3}</span>
                                  )}
                                </div>
                              )}
                            </div>
                            <Tooltip title={t('Delete Chat')} placement="left">
                              <button
                                className="ml-2 text-gray-500 hover:text-red-500 flex-shrink-0"
                                onClick={() => handleDeleteChat(chatId)}
                              >
                                <AiOutlineDelete size={16} />
                              </button>
                            </Tooltip>
                          </li>
                        </Transition>
                        );
                      })}
                    </Fragment>
                  ))}
              </ul>
            ) : (
              <div className="h-[calc(100vh-450px)] flex items-center justify-center">
                <Badge color="gray" className="h-fit">
                  {t('No Chat History')}
                </Badge>
              </div>
            )}
          </div>
        )}
        {currentView === 'AISettings' && (
          <div className="px-2">
            <AISettingsComponent
              style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 10 }}
              backgroundColor={'transparent'}
            />
          </div>
        )}

        {currentView === 'Sources' && (
          <div className="p-3 flex flex-col h-full">
            {isLoadingSources ? (
              <div className="flex justify-center items-center py-10">
                <ApiLoader />
              </div>
            ) : (
              <>
                {/* Jurisdictions Section */}
                <div className="mb-4">
                  <button
                    onClick={() => setIsJurisdictionsOpen(!isJurisdictionsOpen)}
                    className="flex items-center gap-2 mb-2 w-full text-left group"
                  >
                    {isJurisdictionsOpen ? (
                      <PiCaretDownLight className="w-3.5 h-3.5 text-gray-400" />
                    ) : (
                      <PiCaretRightLight className="w-3.5 h-3.5 text-gray-400" />
                    )}
                    <PiGlobeLight className="w-4 h-4 text-gray-400" />
                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">{t('Jurisdictions')}</span>
                  </button>
                  {isJurisdictionsOpen && (
                    <>
                      {Object.keys(jurisdictionsByCountry).length > 0 ? (
                        <div className="space-y-1 ml-1">
                          {Object.entries(jurisdictionsByCountry).map(([countryCode, sources]) => {
                            const isExpanded = expandedCountries[countryCode] !== false;
                            return (
                              <div key={countryCode}>
                                <button
                                  onClick={() => toggleCountryExpand(countryCode)}
                                  className="flex items-center gap-2 w-full px-1 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                  {isExpanded ? (
                                    <PiCaretDownLight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                  ) : (
                                    <PiCaretRightLight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                  )}
                                  <Flag countryCode={countryCode} size="sm" />
                                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{countryName(countryCode)}</span>
                                  <span className="text-[10px] text-gray-400 ml-auto">{sources.length}</span>
                                </button>
                                {isExpanded && (
                                  <div className="ml-6 space-y-0.5 mt-0.5">
                                    {sources.map((src) => {
                                      const selected = isSourceSelected(src);
                                      return (
                                        <button
                                          key={`${src.source_type}-${src.source_identifier}-${src.index_name}`}
                                          onClick={() => toggleSource(src)}
                                          className={`flex items-center gap-2 w-full px-2 py-1.5 rounded text-xs transition-all cursor-pointer
                                            ${selected ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                                        >
                                          <div className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center transition-colors
                                            ${selected ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 dark:border-gray-500'}`}>
                                            {selected && <PiCheckLight className="w-2.5 h-2.5" />}
                                          </div>
                                          <span className="truncate flex-1 text-left">{src.source_name}</span>
                                          {src.document_count != null && (
                                            <span className="text-[10px] text-gray-400 flex-shrink-0">{formatCount(src.document_count)}</span>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 py-2 ml-6">{t('No jurisdictional sources available')}</p>
                      )}
                    </>
                  )}
                </div>

                {/* User Data Sources Section */}
                <div className="mb-4">
                  <button
                    onClick={() => setIsDataSourcesOpen(!isDataSourcesOpen)}
                    className="flex items-center gap-2 mb-2 w-full text-left group"
                  >
                    {isDataSourcesOpen ? (
                      <PiCaretDownLight className="w-3.5 h-3.5 text-gray-400" />
                    ) : (
                      <PiCaretRightLight className="w-3.5 h-3.5 text-gray-400" />
                    )}
                    <PiDatabaseLight className="w-4 h-4 text-gray-400" />
                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">{t('Your Data Sources')}</span>
                  </button>
                  {isDataSourcesOpen && (
                    <>
                      {userDatasources.length > 0 ? (
                        <div className="space-y-0.5 ml-6">
                          {userDatasources.map((src) => {
                            const selected = isSourceSelected(src);
                            const isReady = !src.status || src.status === 'ready' || src.status === 'active';
                            return (
                              <button
                                key={`ds-${src.source_identifier}`}
                                onClick={() => toggleSource(src)}
                                disabled={!isReady}
                                className={`flex items-center gap-2 w-full px-2 py-1.5 rounded text-xs transition-all
                                  ${!isReady ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                  ${selected ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                              >
                                <div className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center transition-colors
                                  ${selected ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 dark:border-gray-500'}`}>
                                  {selected && <PiCheckLight className="w-2.5 h-2.5" />}
                                </div>
                                <PiDatabaseLight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                <span className="truncate flex-1 text-left">{src.source_name || src.source_identifier}</span>
                                {!isReady && (
                                  <span className="text-[10px] text-amber-500 flex-shrink-0">{src.status}</span>
                                )}
                                {isReady && src.document_count != null && (
                                  <span className="text-[10px] text-gray-400 flex-shrink-0">{formatCount(src.document_count)}</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 py-2 ml-6">{t('No data sources uploaded yet')}</p>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        )}

      </div>

      {/* Sources footer with Apply button */}
      {currentView === 'Sources' && (
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 p-3 space-y-2">
          <p className="text-[11px] text-gray-400">
            {localSelectedSources.length === 0
              ? t('Your AI settings jurisdiction will be used')
              : t(`${localSelectedSources.length} source(s) selected`, { total: localSelectedSources.length })}
          </p>
          <Button variant="primary" onClick={handleApplySources} className="w-full">
            {t('Apply')}
          </Button>
        </div>
      )}

    </aside>
  );
};


export default AISidebar;
